import React, { useMemo, useState, useEffect } from 'react';
import { Heart, Sparkles, X, Music, Edit3 } from 'lucide-react';
import '../style/GiftModal.css';
import { fetchWithAuth } from './authService';
import { API_ENDPOINTS } from '../utils/constants';

const GiftModal = ({
  show,
  onClose,
  djName,
  walletCoins,
  onSuccess,
  PartnerId,
  priceData,
  venueId,
}) => {
  const [selectedCoin, setSelectedCoin] = useState(walletCoins);
  const [showFirstCTA, setShowFirstCTA] = useState(true);
  const [coinPacks, setCoinPacks] = useState(priceData);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPriceId, setSelectedPriceId] = useState(null);
  const [selectedPriceItem, setSelectedPriceItem] = useState(null);
  const [isCustomAmountOpen, setIsCustomAmountOpen] = useState(false);
  const [customAmount, setCustomAmount] = useState(50);
  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const showError = message => {
    setErrorMsg(message);
    setIsError(true);
    // Auto hide error after 3 seconds
    setTimeout(() => {
      setIsError(false);
      setErrorMsg('');
    }, 3000);
  };

  // Function to get custom description based on coin amount
  const getCustomDescription = coins => {
    switch (coins) {
      case 100:
        return 'Start the party';
      case 400:
        return 'Keep it rolling';
      case 1000:
        return 'Own the night';
      default:
        return `${coins} coins`;
    }
  };

  useEffect(() => {
    // disable scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      // restore scroll when modal is closed
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Animation interval
  useEffect(() => {
    const interval = setInterval(() => {
      setShowFirstCTA(prev => !prev);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Load Razorpay script
  useEffect(() => {
    const loadRazorpay = async () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    };
    loadRazorpay();
  }, []);

  // Fetch Razorpay config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const configresponse = await fetchWithAuth(API_ENDPOINTS.ORDER_CONFIG);
        if (!configresponse.ok) throw new Error('Failed to fetch config');
        const data = await configresponse.json();
        setConfig(data);
      } catch (error) {
        console.error('Error fetching config:', error);
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!coinPacks) return;
    if (walletCoins < 100) {
      const popularPack = coinPacks.find(pack => pack.isPopular);
      if (popularPack) {
        setSelectedCoin(walletCoins + popularPack.coins);
        setSelectedPriceId(popularPack.id);
        setSelectedPriceItem(popularPack);
      } else {
        setSelectedCoin(walletCoins);
      }
    } else {
      setSelectedCoin(walletCoins);
    }
  }, []);

  // Fetch coin packs
  useEffect(() => {
    if (!coinPacks) return;
    const fetchPrice = async () => {
      try {
        const response = await fetchWithAuth(API_ENDPOINTS.ORDER_PRICE);
        if (!response.ok) throw new Error('Failed to fetch price');
        const data = await response.json();
        // Filter out hidden packs and sort by coins ascending
        const visiblePacks = data
          .filter(pack => !pack.isHidden)
          .sort((a, b) => a.coins - b.coins);
        setCoinPacks(visiblePacks);

        // Set initial selection based on wallet coins
        if (walletCoins < 100) {
          // Find and preselect the popular pack
          const popularPack = visiblePacks.find(pack => pack.isPopular);
          if (popularPack) {
            setSelectedCoin(walletCoins + popularPack.coins);
            setSelectedPriceId(popularPack.id);
            setSelectedPriceItem(popularPack);
          } else {
            setSelectedCoin(walletCoins);
          }
        } else {
          setSelectedCoin(walletCoins);
        }
      } catch (error) {
        console.error('Error fetching prices:', error);
      }
    };
    fetchPrice();
  }, [walletCoins]);

  // Create coin options
  const coinOptions = useMemo(() => {
    const options = [
      {
        totalCoins: walletCoins,
        payNow: 0,
        description: 'Wallet balance',
        isWalletOnly: true,
        priceId: null, // Explicitly set to null
      },
      ...coinPacks.map(pack => {
        // Use custom description if wallet coins are 0, otherwise use default format
        const description =
          walletCoins === 0
            ? getCustomDescription(pack.coins)
            : `Wallet + ${pack.coins} coins`;

        return {
          totalCoins: walletCoins + pack.coins,
          payNow: pack.price,
          description: description,
          isPopular: pack.isPopular,
          originalPack: pack,
          priceId: pack.id, // This should contain the correct ID
        };
      }),
    ];

    return options;
  }, [walletCoins, coinPacks]);

  const makeOrder = async (selectedPriceId = null) => {
    setLoading(true);
    const token = localStorage.getItem('tringAuthToken');
    if (!token) {
      console.error('No auth token found');
      setLoading(false);
      return;
    }

    // Find price item by id
    const priceItem = coinPacks.find(p => p.id === selectedPriceId);
    if (!priceItem) {
      console.error('Price item not found for ID:', selectedPriceId);
      setLoading(false);
      return;
    }

    try {
      const orderresponse = await fetchWithAuth(API_ENDPOINTS.ORDER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          priceId: selectedPriceId,
          partnerId: PartnerId,
          venueId: venueId,
        }),
      });

      if (!orderresponse.ok) throw new Error('Failed to create order');
      const data = await orderresponse.json();
      console.log(data);
      handlePayment(data, priceItem.price);
    } catch (error) {
      console.error('Error creating order:', error);
      showError('Failed to create order');
      setLoading(false);
    }
  };

  // Payment handling
  const handlePayment = (data, amount) => {
    if (!window.Razorpay || !config) {
      showError('Payment gateway not loaded');
      setLoading(false);
      return;
    }

    const phoneNumber = localStorage.getItem('number') || '';
    const options = {
      key: config.key_id,
      amount: amount,
      currency: 'INR',
      name: 'Tringbox',
      description: 'Coin Transaction',
      order_id: data.order_id,
      prefill: {
        contact: phoneNumber,
      },
      handler: function (response) {
        showError('Payment Successful!');
        console.log('successfull');
        setLoading(false); // Add this line
        if (onSuccess) onSuccess();
        onClose();
      },
      theme: {
        color: '#CC151E',
      },
      method: {
        upi: true,
        card: false,
        netbanking: false,
        wallet: false,
        emi: false,
        paylater: false,
      },
    };

    const rzp = new window.Razorpay(options);

    rzp.on('payment.failed', function () {
      showError('Payment Failed!');
      setLoading(false);
    });

    // This is the key fix - handle modal close (when user cancels/backs out)
    rzp.on('modal.close', function () {
      setLoading(false); // Set loading to false when user closes/cancels
    });

    rzp.open();

    // Fallback timeout (optional - you can keep this as backup)
    setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 30000);
  };

  useEffect(() => {
    // Razorpay modal closes via browser back or manual exit
    const resetOnBack = () => {
      setLoading(false);
    };

    window.addEventListener('focus', resetOnBack);
    return () => {
      window.removeEventListener('focus', resetOnBack);
    };
  }, []);

  // Add this function after the handlePayment function
  const sendGiftFromWallet = async coinsToSend => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.PARTNER_GIFT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          partnerId: PartnerId,
          coins: coinsToSend,
          venueId: venueId,
        }),
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(
          `Failed to send gift: ${response.status} ${response.statusText}`
        );
      }

      // Handle success - both 204 and other success statuses
      console.log('Gift sent successfully');
      showError('Gift sent successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error sending gift:', error);
      showError('Failed to send gift. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = option => {
    setSelectedCoin(option.totalCoins);

    if (option.isWalletOnly) {
      // Clear price ID for wallet-only option
      setSelectedPriceId(null);
      setSelectedPriceItem(null);
    } else if (option.priceId) {
      setSelectedPriceId(option.priceId);
      setSelectedPriceItem(option.originalPack);
    }
  };

  const handlePaymentClick = () => {
    console.log(
      'Payment click - Selected Coin:',
      selectedCoin,
      'Wallet Coins:',
      walletCoins,
      'Price ID:',
      selectedPriceId
    );

    if (
      selectedCoin === 0 ||
      (selectedCoin === walletCoins && walletCoins === 0)
    ) {
      showError('Minimum 50 coins can be gifted');
      return;
    }

    // Check if user is trying to send less than 50 coins from wallet
    if (selectedCoin === walletCoins && walletCoins < 50 && walletCoins > 0) {
      showError('Minimum 50 coins can be gifted');
      return;
    }

    if (selectedCoin === walletCoins) {
      // Wallet-only option - send gift directly
      sendGiftFromWallet(walletCoins);
      return;
    }

    if (!selectedPriceId) {
      console.log('No price ID selected');
      showError('Please select a gift amount');
      return;
    }

    console.log('Making order with Price ID:', selectedPriceId);
    makeOrder(selectedPriceId);
  };

  if (!show) return null;

  return (
    <div className="gift-overlay">
      <div className="gift-backdrop" onClick={onClose} />

      <div className="gift-bottom-sheet">
        {/* Header */}
        <div className="gift-header">
          <div className="gift-header-content">
            <div className="gift-header-icon">
              <Heart className="gift-heart-icon" />
            </div>
            <div className="gift-header-text">
              <h3 className="gift-header-title">
                Support {djName} & spread the love
              </h3>
            </div>
          </div>
          <button className="gift-close-btn" onClick={onClose}>
            <X className="gift-close-icon" />
          </button>
        </div>

        {/* Options */}
        <div className="gift-scroll">
          <div className="gift-content">
            {/* Section Title */}
            <div className="gift-section-title">
              <h3 className="gift-section-title-content">Choose Gift Amount</h3>
              {walletCoins > 49 && (
                <button
                  className="edit-custom-btn"
                  onClick={() => setIsCustomAmountOpen(true)}
                  title="Custom amount"
                >
                  <Edit3 className="gift-edit-icon" />
                </button>
              )}
            </div>

            <div className="gift-options">
              {coinOptions.map((option, index) => (
                <button
                  key={`${option.totalCoins}-${index}`}
                  className={`gift-option ${selectedCoin === option.totalCoins ? 'selected' : ''}`}
                  onClick={() => handleOptionSelect(option)}
                  disabled={loading}
                >
                  {option.isPopular && (
                    <div className="popular-badge">Popular</div>
                  )}
                  <div className="gift-option-left">
                    <div className="gift-coin-icon">
                      <img
                        src="/images/coin.svg"
                        alt="coin"
                        style={{
                          width: 25,
                          height: 25,
                        }}
                      />
                    </div>
                    <div className="gift-option-text">
                      <p className="gift-option-main">
                        {option.totalCoins} coins
                      </p>
                      <p className="gift-option-sub">{option.description}</p>
                    </div>
                  </div>
                  <div className="gift-option-right">
                    <span
                      className={`pay-btn ${option.payNow === 0 ? 'pay-wallet' : 'pay-premium'}`}
                    >
                      Pay ₹{option.payNow} now
                      {option.payNow > 0 && (
                        <div className="shimmer-overlay"></div>
                      )}
                    </span>
                    <div
                      className={`radio-btn ${selectedCoin === option.totalCoins ? 'selected' : ''}`}
                    >
                      {selectedCoin === option.totalCoins && (
                        <div className="radio-inner"></div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="cta-container">
          <button
            className="cta-btn"
            onClick={handlePaymentClick}
            disabled={loading}
          >
            <div className="cta-content">
              <div
                className={`cta-text ${showFirstCTA ? 'visible' : 'hidden'}`}
              >
                <Heart className="cta-icon" />
                <span>Send Your Gift Now</span>
                <Sparkles className="cta-icon" />
              </div>
              <div
                className={`cta-text ${!showFirstCTA ? 'visible' : 'hidden'}`}
              >
                <Heart className="cta-icon" />
                <span>Surprise {djName} Now!</span>
                <Sparkles className="cta-icon" />
              </div>
            </div>

            <div className="cta-shimmer"></div>
          </button>
        </div>
      </div>

      {/* Custom Amount Modal */}
      {isCustomAmountOpen && (
        <div className="custom-amount-overlay">
          <div
            className="custom-amount-backdrop"
            onClick={() => setIsCustomAmountOpen(false)}
          />
          <div className="custom-amount-sheet">
            {/* Header */}
            <div className="custom-amount-header">
              <div className="custom-amount-header-content">
                <div className="custom-amount-icon">
                  <Edit3 className="custom-edit-icon" />
                </div>
                <div className="custom-amount-text">
                  <h2 className="custom-amount-title">Custom Gift Amount</h2>
                  <p className="custom-amount-subtitle">
                    Gift from your wallet balance
                  </p>
                </div>
              </div>
              <button
                className="custom-amount-close-btn"
                onClick={() => setIsCustomAmountOpen(false)}
              >
                <X className="custom-close-icon" />
              </button>
            </div>

            {/* Content */}
            <div className="custom-amount-content">
              <div className="wallet-info">
                <p className="wallet-text">
                  Available wallet balance:{' '}
                  <span className="wallet-balance">{walletCoins} coins</span>
                </p>
              </div>
              <div className="input-section">
                <label className="input-label">Enter coins to gift</label>
                <input
                  type="number"
                  min="1"
                  max={walletCoins}
                  value={customAmount}
                  onChange={e => setCustomAmount(e.target.value)}
                  placeholder="Enter amount..."
                  className="custom-input"
                />
                {customAmount &&
                  (parseInt(customAmount) > walletCoins ||
                    parseInt(customAmount) < 50) && (
                    <p className="error-text">
                      Amount must be between 50 and {walletCoins} coins
                    </p>
                  )}
              </div>

              <button
                onClick={() => {
                  if (
                    customAmount &&
                    parseInt(customAmount) >= 50 &&
                    parseInt(customAmount) <= walletCoins
                  ) {
                    // Send gift from wallet with custom amount
                    sendGiftFromWallet(parseInt(customAmount));
                    setIsCustomAmountOpen(false);
                  }
                }}
                disabled={
                  !customAmount ||
                  parseInt(customAmount) > walletCoins ||
                  parseInt(customAmount) < 50 ||
                  loading
                }
                className="custom-submit-btn"
              >
                {`Gift ${customAmount || '0'} Coins`}
              </button>
            </div>
          </div>
        </div>
      )}
      {isError && (
        <div
          style={{
            position: 'fixed',
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            color: 'white',
            background: 'rgba(236, 236, 236, 1)',
            borderRadius: '20px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.32)',
            padding: '16px 24px',
            minWidth: '300px',
            maxWidth: '90vw',
            animation: 'slideDown 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <img
            src="/images/logo.svg"
            style={{ width: '18px', height: '18px', flexShrink: 0 }}
          />
          <div
            style={{
              color: 'black',
              fontSize: '13px',
              lineHeight: '1.4',
              textAlign: 'start',
            }}
          >
            {typeof errorMsg === 'string'
              ? errorMsg
              : errorMsg?.message || 'Something went wrong'}
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftModal;
